import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short'
  });
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;
  const readingTime = data.fullDate.toLocaleTimeString([], { 
    hour: '2-digit', 
    minute: '2-digit' 
  });

  return (
    <div className="bg-white p-4 rounded shadow-lg border border-gray-200">
      <p className="font-medium text-gray-800">{label} at {readingTime}</p>
      {data.glucoseActual && (
        <p className="text-cyan-600">
          Glucose: {data.glucoseActual} ({data.glucose > 0 ? '+' : ''}{data.glucose?.toFixed(1)}% from avg)
        </p>
      )}
      {data.bloodPressureActual && (
        <p className="text-red-600">
          Blood Pressure: {data.bloodPressureActual} ({data.bloodPressure > 0 ? '+' : ''}{data.bloodPressure?.toFixed(1)}% from avg)
        </p>
      )}
      {data.weightActual && (
        <p className="text-green-600">
          Weight: {data.weightActual} ({data.weight > 0 ? '+' : ''}{data.weight?.toFixed(1)}% from avg)
        </p>
      )}
    </div>
  );
};

const CombinedHealthChart = ({ entries, dateRange }) => {
  // Filter entries by date range
  const filteredEntries = entries.filter(entry => {
    if (!dateRange.start && !dateRange.end) return true;
    
    const entryDate = new Date(entry.datetime);
    const startDate = dateRange.start ? new Date(dateRange.start) : null;
    const endDate = dateRange.end ? new Date(dateRange.end) : null;
    
    if (startDate) startDate.setHours(0, 0, 0, 0);
    if (endDate) endDate.setHours(23, 59, 59, 999);
    
    if (startDate && entryDate < startDate) return false;
    if (endDate && entryDate > endDate) return false;
    return true;
  });

  // Extract and process each metric
  const glucoseEntries = filteredEntries
    .filter(entry => entry.type === 'glucose')
    .map(entry => ({
      datetime: entry.datetime,
      value: parseFloat(entry.value),
      actual: entry.value
    }));

  const bloodPressureEntries = filteredEntries
    .filter(entry => entry.type === 'bloodPressure')
    .map(entry => {
      const [systolic] = entry.value.split('/').map(v => parseInt(v.trim()));
      return {
        datetime: entry.datetime,
        value: systolic || 0,
        actual: entry.value
      };
    })
    .filter(entry => entry.value > 0);

  const weightEntries = filteredEntries
    .filter(entry => entry.type === 'weight')
    .map(entry => ({
      datetime: entry.datetime,
      value: parseFloat(entry.value),
      actual: entry.value
    }));

  // Calculate averages for percentage change calculation
  const glucoseValues = glucoseEntries.map(e => e.value);
  const bloodPressureValues = bloodPressureEntries.map(e => e.value);
  const weightValues = weightEntries.map(e => e.value);

  const glucoseAvg = glucoseValues.length > 0 ? glucoseValues.reduce((a, b) => a + b, 0) / glucoseValues.length : 0;
  const bloodPressureAvg = bloodPressureValues.length > 0 ? bloodPressureValues.reduce((a, b) => a + b, 0) / bloodPressureValues.length : 0;
  const weightAvg = weightValues.length > 0 ? weightValues.reduce((a, b) => a + b, 0) / weightValues.length : 0;

  // Calculate percentage change from average
  const getPercentageChange = (value, average) => {
    if (average === 0) return 0;
    return ((value - average) / average) * 100;
  };

  // Combine all unique dates
  const allDates = new Set();
  [...glucoseEntries, ...bloodPressureEntries, ...weightEntries].forEach(entry => {
    allDates.add(entry.datetime);
  });

  // Create combined data points
  const combinedData = Array.from(allDates)
    .map(datetime => {
      const date = new Date(datetime);
      const glucoseEntry = glucoseEntries.find(e => e.datetime === datetime);
      const bloodPressureEntry = bloodPressureEntries.find(e => e.datetime === datetime);
      const weightEntry = weightEntries.find(e => e.datetime === datetime);

      const dataPoint = {
        fullDate: date,
        date: formatDate(datetime),
        datetime
      };

      if (glucoseEntry) {
        dataPoint.glucose = getPercentageChange(glucoseEntry.value, glucoseAvg);
        dataPoint.glucoseActual = `${glucoseEntry.actual} mmol/L`;
      }

      if (bloodPressureEntry) {
        dataPoint.bloodPressure = getPercentageChange(bloodPressureEntry.value, bloodPressureAvg);
        dataPoint.bloodPressureActual = `${bloodPressureEntry.actual} mmHg`;
      }

      if (weightEntry) {
        dataPoint.weight = getPercentageChange(weightEntry.value, weightAvg);
        dataPoint.weightActual = `${weightEntry.actual} kg`;
      }

      return dataPoint;
    })
    .sort((a, b) => a.fullDate - b.fullDate);

  if (combinedData.length === 0) {
    return (
      <div className="h-[400px] flex items-center justify-center text-gray-500">
        No health data available for correlation analysis
      </div>
    );
  }

  // Calculate Y-axis domain based on data range
  const allValues = [];
  combinedData.forEach(d => {
    if (d.glucose !== undefined) allValues.push(d.glucose);
    if (d.bloodPressure !== undefined) allValues.push(d.bloodPressure);
    if (d.weight !== undefined) allValues.push(d.weight);
  });
  
  const minValue = allValues.length > 0 ? Math.min(...allValues) : -10;
  const maxValue = allValues.length > 0 ? Math.max(...allValues) : 10;
  const padding = Math.max(Math.abs(minValue), Math.abs(maxValue)) * 0.1;
  const yMin = Math.floor(minValue - padding);
  const yMax = Math.ceil(maxValue + padding);

  return (
    <div className="h-[400px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={combinedData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="date" 
            angle={-45}
            textAnchor="end"
            height={70}
          />
          <YAxis 
            label={{ value: '% Change from Average', angle: -90, position: 'insideLeft' }}
            domain={[yMin, yMax]}
            tickFormatter={(value) => `${value > 0 ? '+' : ''}${value.toFixed(0)}%`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          
          {/* Zero reference line */}
          <Line
            type="monotone"
            dataKey={() => 0}
            stroke="#6b7280"
            strokeWidth={1}
            strokeDasharray="5,5"
            dot={false}
            connectNulls={true}
            name="Average (0%)"
          />
          
          {glucoseEntries.length > 0 && (
            <Line
              type="monotone"
              dataKey="glucose"
              stroke="#0891b2"
              strokeWidth={0}
              dot={{ fill: '#0891b2', r: 4 }}
              connectNulls={false}
              name="Glucose"
            />
          )}
          
          {bloodPressureEntries.length > 0 && (
            <Line
              type="monotone"
              dataKey="bloodPressure"
              stroke="#dc2626"
              strokeWidth={0}
              dot={{ fill: '#dc2626', r: 4 }}
              connectNulls={false}
              name="Blood Pressure"
            />
          )}
          
          {weightEntries.length > 0 && (
            <Line
              type="monotone"
              dataKey="weight"
              stroke="#16a34a"
              strokeWidth={0}
              dot={{ fill: '#16a34a', r: 4 }}
              connectNulls={false}
              name="Weight"
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CombinedHealthChart;
